import { LocationVarKey } from "../Generated/LocationVarKey.Data";

/**
 * Canonical registry of operator-set external links the bot may share. Each
 * link has its own URL string var + enabled boolean — 14 individual
 * LocationVarKey entries total, for inheritance support.
 *
 * The new Links page reads/writes ONLY the Cortex vars; legacy URL strings
 * and `Has*URL` flags are NOT touched. The migration script (batch 10) seeds
 * the new Cortex vars from legacy values via `legacyURLKey` and
 * `legacyHasURLKey`.
 *
 * See plan: docs/snowy/plans/2026-05-01-cortex-structured-vars.md
 */

export interface URLDef {
  key: URLKey;
  label: string;
  description: string;
  /** Cortex var holding the URL string. */
  urlVarKey: LocationVarKey;
  /** Cortex var holding the enabled boolean. */
  enabledVarKey: LocationVarKey;
  /** Legacy URL string var. Optional — some links have no legacy URL string. */
  legacyURLKey?: LocationVarKey;
  /** Legacy boolean flag var. Optional — FacilityMap has no legacy enabled flag. */
  legacyHasURLKey?: LocationVarKey;
}

export enum URLKey {
  AccountLogin = "account_login",
  Auctions = "auctions",
  Career = "career",
  FacilityMap = "facility_map",
  GoogleReview = "google_review",
  Insurance = "insurance",
  MobileApp = "mobile_app",
  MovingServices = "moving_services",
  PackingSupplies = "packing_supplies",
  PayOnline = "pay_online",
  PrivacyPolicy = "privacy_policy",
  Referral = "referral",
  SizeGuide = "size_guide"
}

/** URLs tied to a toggle — shown on their own Amenity URLs card. */
export const AMENITY_URL_KEYS: readonly URLKey[] = [URLKey.Referral, URLKey.PackingSupplies, URLKey.MovingServices];

export const URLS: readonly URLDef[] = [
  {
    key: URLKey.AccountLogin,
    label: "Account Login",
    description: "URL to the customer account login page.",
    urlVarKey: LocationVarKey.CortexURLAccountLogin,
    enabledVarKey: LocationVarKey.CortexURLAccountLoginEnabled,
    legacyURLKey: LocationVarKey.AccountLoginURL,
    legacyHasURLKey: LocationVarKey.HasAccountLoginPage
  },
  {
    key: URLKey.FacilityMap,
    label: "Facility Map",
    description: "Per-location URL to the facility map (PDF or web page) so tenants can locate their unit.",
    urlVarKey: LocationVarKey.CortexFacilityMapURL,
    enabledVarKey: LocationVarKey.CortexFacilityMapURLEnabled,
    legacyURLKey: LocationVarKey.MapURL
    // No legacy `HasMapURL` — operator sets enabled in PM. URL value
    // backfills from legacy `MapURL`.
  },
  {
    key: URLKey.Career,
    label: "Careers",
    description: "URL to careers / job listings.",
    urlVarKey: LocationVarKey.CortexURLCareer,
    enabledVarKey: LocationVarKey.CortexURLCareerEnabled,
    legacyURLKey: LocationVarKey.CareersURL,
    legacyHasURLKey: LocationVarKey.HasCareerURL
  },
  {
    key: URLKey.GoogleReview,
    label: "Google Review",
    description: "URL where customers leave a Google review (included in review-request messages).",
    urlVarKey: LocationVarKey.CortexURLGoogleReview,
    enabledVarKey: LocationVarKey.CortexURLGoogleReviewEnabled,
    legacyURLKey: LocationVarKey.GoogleReviewURL,
    // `HasGoogleReviewIntegration` gates the legacy post-interaction review-request nudge — the same
    // thing CortexURLGoogleReviewEnabled gates in v3 (ReviewRequest.Nudge.ts). Without this, a
    // migrated org's URL text would come across but the nudge would never fire.
    legacyHasURLKey: LocationVarKey.HasGoogleReviewIntegration
  },
  {
    key: URLKey.PayOnline,
    label: "Pay Online",
    description: "URL to online payment page.",
    urlVarKey: LocationVarKey.CortexURLPayOnline,
    enabledVarKey: LocationVarKey.CortexURLPayOnlineEnabled,
    legacyURLKey: LocationVarKey.PayOnlineURL,
    legacyHasURLKey: LocationVarKey.HasPayOnlineURL
  },
  {
    key: URLKey.PrivacyPolicy,
    label: "Privacy Policy",
    description: "URL to the privacy policy.",
    urlVarKey: LocationVarKey.CortexURLPrivacyPolicy,
    enabledVarKey: LocationVarKey.CortexURLPrivacyPolicyEnabled,
    legacyURLKey: LocationVarKey.PrivacyPolicyURL,
    legacyHasURLKey: LocationVarKey.HasPrivacyPolicyURL
  },
  {
    key: URLKey.Referral,
    label: "Referral Program",
    description: "URL to the referral program page for customers.",
    urlVarKey: LocationVarKey.CortexURLReferral,
    enabledVarKey: LocationVarKey.CortexURLReferralEnabled,
    legacyURLKey: LocationVarKey.ReferralProgramURL,
    legacyHasURLKey: LocationVarKey.HasReferralProgram
  },
  {
    key: URLKey.PackingSupplies,
    label: "Packing Supplies",
    description: "URL to buy or learn about packing supplies at this location.",
    urlVarKey: LocationVarKey.CortexURLPackingSupplies,
    enabledVarKey: LocationVarKey.CortexURLPackingSuppliesEnabled,
    legacyURLKey: LocationVarKey.PackingSuppliesURL,
    legacyHasURLKey: LocationVarKey.HasPackingSupplies
  },
  {
    key: URLKey.MovingServices,
    label: "Moving Services",
    description: "URL to learn about moving services at this location.",
    urlVarKey: LocationVarKey.CortexURLMovingServices,
    enabledVarKey: LocationVarKey.CortexURLMovingServicesEnabled,
    legacyURLKey: LocationVarKey.MovingServicesURL,
    legacyHasURLKey: LocationVarKey.HasMovingServices
  },
  {
    key: URLKey.Insurance,
    label: "Insurance",
    description: "URL to sign up for or learn about this location's insurance / protection plan.",
    urlVarKey: LocationVarKey.CortexURLInsurance,
    enabledVarKey: LocationVarKey.CortexURLInsuranceEnabled,
    legacyURLKey: LocationVarKey.InsuranceURL,
    legacyHasURLKey: LocationVarKey.HasInsuranceWebsiteURL
  },
  {
    key: URLKey.Auctions,
    label: "Auctions",
    description: "URL to this location's public auction listings.",
    urlVarKey: LocationVarKey.CortexURLAuctions,
    enabledVarKey: LocationVarKey.CortexURLAuctionsEnabled,
    legacyURLKey: LocationVarKey.AuctionsURL,
    legacyHasURLKey: LocationVarKey.HasAuctions
  },
  {
    key: URLKey.MobileApp,
    label: "Mobile App",
    description: "URL to download this location's mobile app.",
    urlVarKey: LocationVarKey.CortexURLMobileApp,
    enabledVarKey: LocationVarKey.CortexURLMobileAppEnabled,
    legacyURLKey: LocationVarKey.MobileAppDownloadURL,
    legacyHasURLKey: LocationVarKey.HasMobileApp
  },
  {
    key: URLKey.SizeGuide,
    label: "Size Guide",
    description: "URL to the unit-size selection guide.",
    urlVarKey: LocationVarKey.CortexURLSizeGuide,
    enabledVarKey: LocationVarKey.CortexURLSizeGuideEnabled,
    // The link lives under legacy `SizeVisualsURL` (there was never a `SizeGuideURL`).
    legacyURLKey: LocationVarKey.SizeVisualsURL,
    legacyHasURLKey: LocationVarKey.HasSizeGuideURL
  }
];

export function findURLDef(key: URLKey): URLDef | undefined {
  return URLS.find((l) => l.key === key);
}
